import {
  ServiceEvent,
  ServiceEventCreateDTO,
  useActivateServiceEventMutation,
  useCreateServiceEventMutation,
  useDeleteServiceEventMutation,
  useUpdateServiceEventMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseServiceEventMutationProps = {}

export type UseServiceEventMutationResult = {
  createServiceEvent: (serviceEvent: Partial<ServiceEvent>, callback: EntityMutationCallback<ServiceEvent> | undefined) => void
  updateServiceEvent: (serviceEvent: Partial<ServiceEvent>, callback: EntityMutationCallback<ServiceEvent> | undefined) => void
  deleteServiceEvent: (serviceEventId: string, callback: EntityMutationCallback | undefined) => void

  activateServiceEvent: (serviceEvent: Partial<ServiceEventCreateDTO>, callback: EntityMutationCallback<ServiceEvent> | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useServiceEventMutation = (props: UseServiceEventMutationProps = {}): UseServiceEventMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createServiceEvent, {isLoading: isCreating}] = useCreateServiceEventMutation()
  const [_updateServiceEvent, {isLoading: isUpdating}] = useUpdateServiceEventMutation()
  const [_deleteServiceEvent, {isLoading: isDeleting}] = useDeleteServiceEventMutation()

  const [_activateServiceEvent, {isLoading: isActivating}] = useActivateServiceEventMutation()

  useEffect(() => {
    _setCreating(isCreating || isActivating)
  }, [isCreating, isActivating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useServiceEventUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.serviceevent", "Service Event"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useServiceEventUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.serviceevent", "Service Event"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useServiceEventUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.serviceevent", "Service Event"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ServiceEvent>, callback: EntityMutationCallback<ServiceEvent> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceevent", "Service Event") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<ServiceEvent> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceevent", "Service Event") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceevent", "Service Event") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((serviceEventId: string, callback: EntityMutationCallback<ServiceEvent> | undefined) => {
    const promise = _deleteServiceEvent({id: serviceEventId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteServiceEvent, handleDeleteResult])

  const createServiceEvent = useCallback((serviceEvent: Partial<ServiceEvent>, callback: EntityMutationCallback<ServiceEvent> | undefined) => {
    const promise = _createServiceEvent(serviceEvent).unwrap()
    handleCreateResult(promise, callback)
  }, [_createServiceEvent, handleCreateResult])

  const updateServiceEvent = useCallback((serviceEvent: Partial<ServiceEvent>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateServiceEvent(serviceEvent).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateServiceEvent, handleUpdateResult])

  const deleteServiceEvent = useCallback((serviceEventId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.serviceevent", "Service Event"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.serviceevent.confirm", "Are you sure you want to delete this service event?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(serviceEventId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  const activateServiceEvent = useCallback((serviceEvent: Partial<ServiceEventCreateDTO>, callback: EntityMutationCallback<ServiceEvent> | undefined) => {
    const promise = _activateServiceEvent(serviceEvent).unwrap()
    handleCreateResult(promise, callback)
  }, [_activateServiceEvent, handleCreateResult])

  return {
    createServiceEvent: createServiceEvent,
    updateServiceEvent: updateServiceEvent,
    deleteServiceEvent: deleteServiceEvent,

    activateServiceEvent: activateServiceEvent,

    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
